import { getItemsInPreferredOrder, UserEditorPreference } from "client_legacy/Documents/officeDocumentsOpenPreference";
import {
	downloadAsZip,
	downloadDocument,
	downloadDocumentInline
} from "client_legacy/Documents/SharePoint/Actions/downloadAction";
import {
	DocumentItemViewModel,
	DocumentItemType,
	DocumentsViewModel,
	DocumentsViewType,
	DocumentTemplateModel,
	FileViewModel,
	FolderViewModel,
	ListLocationModel
} from "client/Api/Documents/SharePoint/common";
import {
	openFileInSharePointViewer,
	openFileInWopi
} from "client_legacy/Documents/SharePoint/documentOpener";
import { open as openInLocalApp } from "client_legacy/Documents/SharePoint/Actions/openInLocalAppAction";
import {
	calculate,
	calculateForExtension,
	getOpenLocalizations,
	ONE_NOTE_DOCUMENT_VIRTUAL_EXTENSION
} from "client_legacy/Documents/SharePoint/documentOpenPossibilities";
import { Folderish } from "client_legacy/Documents/SharePoint/models";
import {
	ActionKind,
	deselectAllTitle
} from "client_legacy/ndaw/actions";
import { localize } from "client_legacy/ndaw/culture";
import { documentsConfiguration } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import { OfficeDocumentEditor, NewDocumentType } from "client/Api/Documents/common";
import { showOpeningInLocalEditorWithOptionToOpenInOnlineEditor } from "client_legacy/Documents/misc";
import { previewImageInDialogAction } from "client_legacy/Documents/SharePoint/Actions/previewImageInDialogAction";
import { AlertManager } from "client/Hooks";
import { convertToLocationModel } from "client_legacy/Documents/SharePoint/listLocationModelProvider";
import { deleteItems } from "client_legacy/Documents/SharePoint/Actions/deleteItemsAction";
import { EmailClient, getEffectiveEmailClient } from "client_legacy/Email/emailClient";
import { sendSharePointFileAsAttachment } from "client_legacy/Email/SendFile/sendFile";
import { openManagePermissionsDialog } from "./Dialogs/ManagePermssionsDialog/ManagePermssionsDialog";
import { removeBookmarks } from "client/Api/Documents/Bookmarks/bookmarks";
import { DocumentTemplateState } from "./useDocumentTemplates";
import {
	canCopy,
	canDelete,
	canDownloadSingleFile,
	canManagePermissions,
	canMoveTo,
	canRename,
	canShareLink,
	canCheckVersionHistory
} from "./actionsRules";
import { openVersionDialog } from "./Dialogs/VersionDialog";
import { SyncClient } from "client/Api/SharePointSettings/sharepointSettings";
import {
	ActionWithQuickActionIndex,
	ToolbarAction,
	ToolbarActionGroup
} from "client/Components";
import { openRenameDialog } from "./Dialogs/RenameDialog";
import { openNewFolderDialog } from "./Dialogs/NewFolderDialog";
import { openInviteUserDialog } from "./Dialogs/InviteUserDialog";
import { openShareALinkDialog } from "./Dialogs/ShareLink/ShareALinkDialog";
import { openSetOfficePreferenceDialog } from "../SetOfficePreferenceDialog";
import { openNewDocumentDialog } from "./Dialogs/NewDocumentDialog";
import { openSyncWithOneDriveDialog } from "./Dialogs/SyncWithOneDriveDialog";
import { getDocumentKind } from "client_legacy/Documents/fileExtensions";
import { sortBy } from "client_legacy/ndaw/arrayHelpers";
import { BookmarkEvent, publish } from "client_legacy/Documents/Bookmarks/events";
import { getDownloadFileUrlPath } from "client_legacy/Documents/SharePoint/UrlsProviders/documentsItemUrlsProviders";
import { openUrlFile } from "../openUrlFile";

export type SharePointDocumentActionContext = {
	view: DocumentsViewModel;
	folder: Folderish;
	selection: readonly DocumentItemViewModel[];

	syncClient: SyncClient;
	maxNumberOfFilesToDownloadAsZip: number;
	isSharingViaLinkEnabled: boolean;
	isVersioningEnabled: boolean;
	isInSearchResults: boolean;

	isMobile: boolean;
	isInSelectMode: boolean;
};

export type SharePointDocumentAction = ActionWithQuickActionIndex<SharePointDocumentActionContext>;

export type SharePointDocumentActionAndOthers = ToolbarAction<SharePointDocumentActionContext>;

const _newGroup: ToolbarActionGroup = {
	kind: "regular",
	name: "new-group",
	title: localize("_New"),
	icon: "plus",
	isPrimary: true
};

const _manageGroup: ToolbarActionGroup = {
	kind: "virtual",
	name: "manage-group"
};

const _openGroup: ToolbarActionGroup = {
	kind: "alternative",
	name: "open-group",
	icon: "folder-open",
	title: localize("_Open")
};

const _uploadGroup: ToolbarActionGroup = {
	kind: "regular",
	name: "upload-group",
	icon: "upload",
	title: localize("_Upload")
};

export function getNewActions(
	documentTemplateState: DocumentTemplateState,
	shouldHideOnSelection: boolean
): readonly SharePointDocumentActionAndOthers[] {
	const result: SharePointDocumentActionAndOthers[] = [
		{
			kind: ActionKind.Regular,
			group: _newGroup,
			name: "new-folder",
			icon: { kind: "svg", name: "folder" },
			title: localize("_Folder"),
			canExecute: (c) => {
				return (c.folder.permissions?.canCreate ?? false)
					&& !c.isInSearchResults
					&& (!shouldHideOnSelection || c.selection.length === 0);
			},
			execute: (context) => {
				openNewFolderDialog({
					parentFolder: context.folder
				});
			}
		},
		{
			kind: ActionKind.Divider,
			group: _newGroup,
			name: "new-divider",
			canExecute: (c) => {
				return (c.folder.permissions?.canCreate ?? false)
					&& !c.isInSearchResults
					&& (!shouldHideOnSelection || c.selection.length === 0);
			}
		},
		{
			kind: ActionKind.Regular,
			group: _newGroup,
			name: "word",
			icon: { kind: "svg", name: "docx" },
			title: localize("_WordDocument"),
			canExecute: (c) => {
				return (c.folder.permissions?.canCreate ?? false)
					&& !c.isInSearchResults
					&& (!shouldHideOnSelection || c.selection.length === 0);
			},
			execute: (context) => {
				openNewDocumentDialog({
					parentFolder: context.folder,
					documentType: NewDocumentType.Word
				});
			}
		},
		{
			kind: ActionKind.Regular,
			group: _newGroup,
			name: "excel",
			icon: { kind: "svg", name: "xlsx" },
			title: localize("_ExcelWorkbook"),
			canExecute: (c) => {
				return (c.folder.permissions?.canCreate ?? false)
					&& !c.isInSearchResults
					&& (!shouldHideOnSelection || c.selection.length === 0);
			},
			execute: (context) => {
				openNewDocumentDialog({
					parentFolder: context.folder,
					documentType: NewDocumentType.Excel
				});
			}
		},
		{
			kind: ActionKind.Regular,
			group: _newGroup,
			name: "power-point",
			icon: { kind: "svg", name: "powerpoint" },
			title: localize("_PowerPointPresentation"),
			canExecute: (c) => {
				return (c.folder.permissions?.canCreate ?? false)
					&& !c.isInSearchResults
					&& (!shouldHideOnSelection || c.selection.length === 0);
			},
			execute: (context) => {
				openNewDocumentDialog({
					parentFolder: context.folder,
					documentType: NewDocumentType.PowerPoint
				});
			}
		},
		{
			kind: ActionKind.Regular,
			group: _newGroup,
			name: "one-note",
			icon: { kind: "svg", name: "onenote" },
			title: localize("_OneNoteNotebook"),
			canExecute: (c) => {
				const {
					canBeOpenedOnline,
					canBeOpenedLocally
				} = calculateForExtension(ONE_NOTE_DOCUMENT_VIRTUAL_EXTENSION);

				return (c.folder.permissions?.canCreate ?? false)
					&& !c.isInSearchResults
					&& (canBeOpenedOnline || canBeOpenedLocally)
					&& (!shouldHideOnSelection || c.selection.length === 0);
			},
			execute: (context) => {
				openNewDocumentDialog({
					parentFolder: context.folder,
					documentType: NewDocumentType.OneNote
				});
			}
		}
	];

	if (documentTemplateState.isLoading || documentTemplateState.templates.length >= 1) {
		result.push(
			{
				kind: ActionKind.Divider,
				group: _newGroup,
				name: "new-templates-divider",
				canExecute: (c) => {
					return (c.folder.permissions?.canCreate ?? false)
						&& !c.isInSearchResults
						&& (!shouldHideOnSelection || c.selection.length === 0);
				}
			}
		);
	}

	if (documentTemplateState.isLoading) {
		result.push({
			kind: ActionKind.BusyIndicator,
			group: _newGroup,
			name: "new-templates-loader",
			canExecute: () => true
		});
	} else if (documentTemplateState.templates.length >= 1) {
		const sortOrder: readonly NewDocumentType[] = [
			NewDocumentType.Word,
			NewDocumentType.PowerPoint,
			NewDocumentType.Excel
		];

		const sortedTemplates: readonly DocumentTemplateModel[] = sortBy(
			documentTemplateState.templates,
			(x): number => {
				const index: number = sortOrder.indexOf(x.documentKind);
				return index === -1
					? sortOrder.length
					: index;
			}
		);

		result.push(
			...sortedTemplates
				.map((x): SharePointDocumentAction => {

					return {
						kind: ActionKind.Regular,
						group: _newGroup,
						name: x.name,
						icon: {
							kind: "svg",
							name: getDocumentKind(DocumentItemType.File, x.resultingExtension)
						},
						title: x.name,
						canExecute: (c) => {
							return (c.folder.permissions?.canCreate ?? false)
								&& !c.isInSearchResults
								&& (!shouldHideOnSelection || c.selection.length === 0);
						},
						execute: (c) => {
							openNewDocumentDialog({
								parentFolder: c.folder,
								template: x
							});
						}
					};
				})
		);
	}

	return result;
}

export type FolderActionHandlers = {
	enableSelectMode(): void;
	disableSelectMode(): void;
	selectAll(): void;
	deselectAll(): void;
	uploadFolder(): void;
	uploadFiles(): void;
};

export function getFolderActions(
	documentTemplateState: DocumentTemplateState,
	itemsOnPage: readonly DocumentItemViewModel[],
	handlers: FolderActionHandlers,
	includeNewActions: boolean
): readonly SharePointDocumentActionAndOthers[] {

	return [
		...getSharePointListActions<DocumentItemViewModel, SharePointDocumentActionContext>(
			itemsOnPage,
			{
				deselectAll: handlers.deselectAll,
				disableSelectMode: handlers.disableSelectMode,
				enableSelectMode: handlers.enableSelectMode,
				selectAll: handlers.selectAll
			}
		),
		...(includeNewActions ? getNewActions(documentTemplateState, true) : []),
		{
			kind: ActionKind.Regular,
			name: "upload-folder",
			icon: {
				kind: "svg",
				name: "folder"
			},
			title: localize("_Folder"),
			group: _uploadGroup,
			canExecute: (c) => {
				return (c.folder.permissions?.canCreate ?? false)
					&& !c.isInSearchResults
					&& c.selection.length === 0;
			},
			execute: () => {
				handlers.uploadFolder();
			}
		},
		{
			kind: ActionKind.Regular,
			name: "upload-files",
			icon: "file",
			title: localize("_Files"),
			group: _uploadGroup,
			canExecute: (c) => {
				return (c.folder.permissions?.canCreate ?? false)
					&& !c.isInSearchResults
					&& c.selection.length === 0;
			},
			execute: () => {
				handlers.uploadFiles();
			}
		},
		{
			kind: ActionKind.Regular,
			name: "sync",
			icon: "arrows-rotate",
			title: localize("_Sync"),
			canExecute: (c) => {
				return !c.isMobile
					&& c.view.type === DocumentsViewType.EditableView
					&& !c.isInSearchResults
					&& c.syncClient !== SyncClient.None
					&& c.selection.length === 0;
			},
			execute: (context) => {
				openSyncWithOneDriveDialog({
					syncClient: context.syncClient as Exclude<SyncClient, SyncClient.None>,
					view: context.view,
					folderId: context.folder.id
				});
			}
		},
	];
}

export type ListActionHandlers = {
	enableSelectMode: () => void;
	disableSelectMode: () => void;
	deselectAll: () => void;
	selectAll: () => void;
};

export type ListActionContext<TItem> = {
	selection: readonly TItem[];
	isMobile: boolean;
	isInSelectMode: boolean;
};

export function getSharePointListActions<TItem, TModel extends ListActionContext<TItem>>(
	itemsOnPage: readonly TItem[],
	handlers: ListActionHandlers
): readonly ToolbarAction<TModel>[] {
	return [
		{
			kind: ActionKind.Regular,
			name: "select",
			title: localize("_Select"),
			icon: "hand-pointer",
			canExecute: (c) => {
				return c.isMobile
					&& !c.isInSelectMode
					&& itemsOnPage.length > 0;
			},
			execute: () => {
				handlers.enableSelectMode();
			}
		},
		{
			kind: ActionKind.Regular,
			name: "cancel",
			title: localize("_Cancel"),
			icon: "xmark",
			canExecute: (c) => {
				return c.isMobile && c.isInSelectMode;
			},
			execute: () => {
				handlers.disableSelectMode();
			}
		},
		{
			kind: ActionKind.Regular,
			name: "deselect-all",
			title: (c) => deselectAllTitle(c.selection),
			icon: "circle-xmark",
			canExecute: (c) => c.selection.length > 0 && (!c.isMobile || c.isInSelectMode),
			execute: () => handlers.deselectAll()
		},
		{
			kind: ActionKind.Divider,
			name: "deselect-all-divider",
			canExecute: (c) => !c.isMobile
				&& c.selection.length > 0
		},
		{
			kind: ActionKind.Regular,
			name: "select-all",
			title: localize("_SelectAll"),
			icon: "circle-check",
			canExecute: (c) => {
				return c.isMobile
					&& c.isInSelectMode
					&& c.selection.length !== itemsOnPage.length;
			},
			execute: () => {
				handlers.selectAll();
			}
		}
	];
}

export type OpenActionHandlers = {
	openFolder(folder: FolderViewModel): void;
	selectItem(item: DocumentItemViewModel): void;
};

export function getOpenActions(
	handlers: OpenActionHandlers,
	itemsOnPage: readonly DocumentItemViewModel[],
	alertManager: AlertManager,
	editorPreference?: UserEditorPreference
): readonly SharePointDocumentAction[] {
	return [
		{
			kind: ActionKind.Regular,
			name: "open-folder",
			group: _openGroup,
			title: localize("_Open"),
			icon: "folder-open",
			canExecute: (context) => {
				const { selection } = context;
				if (selection.length !== 1) {
					return false;
				}

				return selection[0].itemType === DocumentItemType.Folder
					&& selection[0].permissions.canView;
			},
			execute: (context) => {
				// We assume always the current server
				handlers.openFolder(context.selection[0] as FolderViewModel);
			}
		},
		...getOpenOfficeDocActions(
			alertManager,
			editorPreference
		),
		{
			kind: ActionKind.Regular,
			name: "open-url-file",
			group: _openGroup,
			title: localize("_Open"),
			icon: "folder-open",
			canExecute: (context) => {
				const { selection } = context;
				if (selection.length !== 1) {
					return false;
				}

				const item: DocumentItemViewModel = selection[0];
				if (item.itemType !== DocumentItemType.File) {
					return false;
				}

				return calculate(item).canBeOpenedAsExternalUrl;
			},
			execute: guardAgainstInfectedDocuments(
				async (c) => {
					const item: DocumentItemViewModel = c.selection[0];
					if (item.itemType !== DocumentItemType.File) {
						throw new Error("The document is not a file.");
					}

					const url: string = getDownloadFileUrlPath(item.id, item.location);
					await openUrlFile(url, alertManager);
				},
				alertManager
			)
		},
		{
			kind: ActionKind.Regular,
			name: "open-in-online-viewer",
			group: _openGroup,
			title: localize("_OpenInAppName")
				.replace(
					"{0}",
					localize("_OnlineViewer")
				),
			icon: "folder-open",
			canExecute: (context) => {
				const { selection } = context;

				if (selection.length !== 1) {
					return false;
				}

				const item: DocumentItemViewModel = selection[0];
				if (item.itemType !== DocumentItemType.File) {
					return false;
				}

				return calculate(item)
					.canBeOpenedInSharePointViewer;
			},
			execute: guardAgainstInfectedDocuments(
				(c) => {
					const item: DocumentItemViewModel = c.selection[0];

					if (item.itemType !== DocumentItemType.File) {
						throw new Error("The document is not a file.");
					}

					openFileInSharePointViewer(item);
				},
				alertManager
			)
		},
		{
			kind: ActionKind.Regular,
			name: "open-in-browser",
			group: _openGroup,
			title: localize("_Open"),
			icon: "folder-open",
			canExecute: (context) => {
				const { selection } = context;
				if (selection.length !== 1) {
					return false;
				}

				const item: DocumentItemViewModel = selection[0];
				if (item.itemType === DocumentItemType.Folder) {
					return false;
				}

				return calculate(item)
					.canBeOpenedInTheBrowser;
			},
			execute: guardAgainstInfectedDocuments(
				(c) => {
					alertManager.showInfo(localize("_CannotOpenThisFileYet"));
					downloadDocumentInline(c.selection[0]);
				},
				alertManager
			)
		},
		{
			kind: ActionKind.Regular,
			name: "open-in-image-preview-dialog",
			group: _openGroup,
			title: localize("_Open"),
			icon: "folder-open",
			canExecute: (context) => {
				const { selection } = context;

				if (selection.length !== 1) {
					return false;
				}

				const item: DocumentItemViewModel = selection[0];
				if (item.itemType === DocumentItemType.Folder) {
					return false;
				}

				return calculate(item)
					.canBeOpenedInImagePreviewDialog;
			},
			execute: guardAgainstInfectedDocuments(
				(context) => {
					previewImageInDialogAction.execute(
						context.selection[0],
						itemsOnPage,
						handlers.selectItem,
						context
					);
				},
				alertManager
			)
		}
	];
}

const _setPreferenceAction: SharePointDocumentAction = {
	kind: ActionKind.Regular,
	group: _openGroup,
	name: "set-preference",
	title: localize("_SetPreference"),
	canExecute: (context) => {
		const {
			selection,
			view,
			folder
		} = context;

		if (view.type === DocumentsViewType.SharedWithMeView
			&& folder.isRoot
		) {
			return false;
		}

		if (selection.length !== 1) {
			return false;
		}

		const item: DocumentItemViewModel = selection[0];
		if (item.itemType === DocumentItemType.Folder) {
			return false;
		}
		const { canBeOpenedLocally, canBeOpenedOnline } = calculate(item);

		if (!canBeOpenedLocally && !canBeOpenedOnline) {
			return false;
		}

		return true;
	},
	execute: () => {
		openSetOfficePreferenceDialog();
	},
	icon: "folder-open",
};

const _deleteAction: SharePointDocumentAction = {
	kind: ActionKind.Regular,
	group: _manageGroup,
	name: "delete",
	title: localize("_Delete"),
	icon: "trash-can",
	canExecute: (context) => {
		const {
			view,
			folder,
			selection
		} = context;

		return canDelete(
			selection,
			view,
			folder.isRoot
		);
	},
	execute: (context) => {
		const { selection } = context;
		// We only support deleting from the same location, however we cannot use view.Id because
		// a user can navigate through folders from "SharedWithMe". These files/folders are in a different
		// list then the "SharedWithMe" view.
		const locationModel: ListLocationModel = convertToLocationModel(selection[0]!.location);

		deleteItems(
			locationModel,
			selection
		);
	}
};

const _renameAction: SharePointDocumentAction = {
	kind: ActionKind.Regular,
	group: _manageGroup,
	name: "rename",
	title: localize("_Rename"),
	canExecute: (context) => canRename(context.selection, context.view),
	execute: (context) => {
		openRenameDialog({
			item: context.selection[0]
		});
	},
	icon: "pen"
};

function createMultiDownloadAction(
	alertManager: AlertManager
): SharePointDocumentAction {
	return {
		kind: ActionKind.Regular,
		name: "multi-download",
		title: localize("_Download"),
		canExecute: (context) => {
			const {
				view,
				selection,
				maxNumberOfFilesToDownloadAsZip
			} = context;

			if (view.type !== DocumentsViewType.EditableView) {
				return false;
			}

			if (selection.length <= 1 || selection.length > maxNumberOfFilesToDownloadAsZip) {
				return false;
			}

			return selection.every((doc) => {
				return doc.itemType === DocumentItemType.File && doc.permissions.canDownload;
			});
		},
		execute: guardAgainstInfectedDocuments(
			(context) => {
				downloadAsZip(context.selection);
			},
			alertManager
		),
		icon: "download"
	};
}

function createRemoveBookmarkAction(
	alertManager: AlertManager
): SharePointDocumentAction {
	return {
		kind: ActionKind.Regular,
		name: "remove-bookmark",
		title: localize("_RemoveBookmark"),
		canExecute: (context) => {
			const {
				view,
				selection
			} = context;

			return selection.length > 0
				&& selection.every((x) => x.bookmarkId !== null)
				&& !(
					view.type === DocumentsViewType.SharedWithMeView
					&& selection[0].itemType === DocumentItemType.Folder
				);
		},
		execute: (context) => {
			const {
				selection
			} = context;

			const bookmarkIds: number[] = selection.map(x => x.bookmarkId!);
			const bookmarkNames: string[] = selection.map(x => "'" + x.name + "'");

			removeBookmarks(bookmarkIds)
				.then(() => {
					alertManager.showSuccess(
						localize("_BookmarksRemovedForItemNames")
							.replace("{ItemNames}", bookmarkNames.join(", "))
					);

					publish(BookmarkEvent.Deleted, bookmarkIds);
				});
		},
		icon: "star",
		quickActionIndex: 1
	};
}

type AddBookmarkHandler = (item: DocumentItemViewModel) => void;

function createAddBookmarkAction(handler: AddBookmarkHandler): SharePointDocumentAction {
	return {
		kind: ActionKind.Regular,
		name: "add-bookmark",
		title: localize("_AddBookmark"),
		canExecute: (context) => {
			const {
				view,
				selection
			} = context;

			return selection.length === 1
				&& selection[0].bookmarkId === null
				&& !(
					view.type === DocumentsViewType.SharedWithMeView
					&& selection[0].itemType === DocumentItemType.Folder
				);
		},
		execute: (context) => {
			handler(context.selection[0]);
		},
		icon: "star-filled",
		quickActionIndex: 2
	};
};

const _permissionsAction: SharePointDocumentAction = {
	kind: ActionKind.Regular,
	name: "share-settings",
	title: localize("_Permissions"),
	canExecute: (context) => canManagePermissions(context.selection),
	execute: (context) => {
		openManagePermissionsDialog({
			document: context.selection[0]
		});
	},
	icon: "key"
};

function createInviteUserAction(
	alertManager: AlertManager
): SharePointDocumentAction {
	return {
		kind: ActionKind.Regular,
		name: "invite-user",
		title: localize("_InviteUser"),
		canExecute: (context) => {
			return context.selection
				.length === 1;
		},
		execute: guardAgainstInfectedDocuments(
			(context) => {
				openInviteUserDialog({
					document: context.selection[0]
				});
			},
			alertManager
		),
		icon: "user-plus"
	};
}

function createDownloadAction(
	alertManager: AlertManager
): SharePointDocumentAction {
	return {
		kind: ActionKind.Regular,
		name: "download",
		title: localize("_Download"),
		canExecute: (context) => canDownloadSingleFile(context.selection),
		execute: guardAgainstInfectedDocuments(
			(context) => {
				downloadDocument(context.selection[0]);
			},
			alertManager
		),
		icon: "download"
	};
}

function createShareLinkAction(
	alertManager: AlertManager
): SharePointDocumentAction {
	return {
		kind: ActionKind.Regular,
		name: "share-file",
		title: localize("_ShareLink"),
		canExecute: (context) => {
			const {
				view,
				selection,
				isSharingViaLinkEnabled
			} = context;

			return canShareLink(selection, view, isSharingViaLinkEnabled);
		},
		execute: guardAgainstInfectedDocuments(
			(context) => {
				openShareALinkDialog({
					document: context.selection[0]
				});
			},
			alertManager
		),
		icon: "link",
		quickActionIndex: 0
	};
}

function createAttachToEmailAction(
	alertManager: AlertManager
): SharePointDocumentAction {
	return {
		kind: ActionKind.Regular,
		name: "attach-to-email",
		title: localize("_AttachToEmail"),
		canExecute: (context) => {
			const { selection } = context;

			const emailClient: EmailClient = getEffectiveEmailClient();
			return emailClient.supportsEditDraft
				&& selection.length === 1
				&& selection[0].itemType === DocumentItemType.File; // folder or OneNote are not supported
		},
		execute: guardAgainstInfectedDocuments(
			(context) => {
				const emailClient: EmailClient = getEffectiveEmailClient();
				if (!emailClient.supportsEditDraft) {
					throw new Error("Current email app launcher does not support edit drafts.");
				}

				const document: DocumentItemViewModel = context.selection[0];
				sendSharePointFileAsAttachment(
					document.id,
					convertToLocationModel(document.location),
					emailClient
				);
			},
			alertManager
		),
		icon: "envelope"
	};
}

export type SelectionActionHandlers = OpenActionHandlers & {
	onMoveItems: (selection: readonly DocumentItemViewModel[]) => void;
	onCopyItems: (selection: readonly DocumentItemViewModel[]) => void;
	addBookmark: AddBookmarkHandler;
};

export type ActionHandlers = SelectionActionHandlers & FolderActionHandlers;

export function getSelectionActions(
	handlers: SelectionActionHandlers,
	itemsOnPage: readonly DocumentItemViewModel[],
	alertManager: AlertManager,
	editorPreference: UserEditorPreference
): readonly SharePointDocumentAction[] {

	return [
		...getOpenActions(
			handlers,
			itemsOnPage,
			alertManager,
			editorPreference
		),
		_setPreferenceAction,
		createShareLinkAction(alertManager),
		createInviteUserAction(alertManager),
		_deleteAction,
		_renameAction,
		{
			kind: ActionKind.Regular,
			group: _manageGroup,
			name: "move",
			title: localize("_MoveTo"),
			canExecute: (context) => canMoveTo(context.selection, context.view),
			execute: guardAgainstInfectedDocuments(
				(context) => handlers.onMoveItems(context.selection),
				alertManager
			),
			icon: "arrows-up-down-left-right"
		},
		{
			kind: ActionKind.Regular,
			group: _manageGroup,
			name: "copy-to",
			title: localize("_CopyTo"),
			canExecute: (context) => canCopy(context.selection, context.view),
			execute: guardAgainstInfectedDocuments(
				(context) => handlers.onCopyItems(context.selection),
				alertManager
			),
			icon: "copy"
		},
		{
			kind: ActionKind.Regular,
			name: "download",
			title: localize("_Download"),
			canExecute: (context) => {
				const {
					selection
				} = context;

				return selection.length === 1
					&& selection[0].itemType === DocumentItemType.File
					&& selection[0].permissions.canDownload;//We can download only single file
			},
			execute: guardAgainstInfectedDocuments(
				(context) => {
					downloadDocument(context.selection[0]);
				},
				alertManager
			),
			icon: "download"
		},
		createMultiDownloadAction(alertManager),
		createAddBookmarkAction(handlers.addBookmark),
		createRemoveBookmarkAction(alertManager),
		{
			kind: ActionKind.Regular,
			name: "versions",
			title: localize("_VersionHistory"),
			canExecute: (context) => {
				const {
					selection,
					isVersioningEnabled
				} = context;

				return canCheckVersionHistory(selection, isVersioningEnabled);
			},
			execute: (context) => {
				openVersionDialog({
					item: context.selection[0] as FileViewModel
				});
			},
			icon: "clock-rotate-left"
		},
		createAttachToEmailAction(alertManager),
		_permissionsAction,
	];
}

function getOpenOfficeDocActions(
	alertManager: AlertManager,
	editorPreference: UserEditorPreference | undefined
): readonly SharePointDocumentAction[] {
	const { sharePoint } = documentsConfiguration();

	return getItemsInPreferredOrder<SharePointDocumentAction>(
		sharePoint.defaultOfficeDocumentEditor,
		[
			[
				OfficeDocumentEditor.Online,
				() => ({
					kind: ActionKind.Regular,
					name: "open-in-online-editor",
					group: _openGroup,
					title: (c) => {
						const { selection } = c;
						if (selection.length !== 1) {
							return "";
						}

						const item: DocumentItemViewModel = selection[0];
						if (item.itemType === DocumentItemType.Folder) {
							return "";
						}

						return getOpenLocalizations(item)
							.online
							.action;
					},
					icon: "folder-open",
					canExecute: (c) => {
						const { selection } = c;
						if (selection.length !== 1) {
							return false;
						}

						const item: DocumentItemViewModel = selection[0];
						if (item.itemType === DocumentItemType.Folder) {
							return false;
						}
						return calculate(item).canBeOpenedOnline;
					},
					execute: guardAgainstInfectedDocuments(
						(c) => {
							const item: DocumentItemViewModel = c.selection[0] as Exclude<DocumentItemViewModel, FolderViewModel>;
							openFileInWopi(item);
						},
						alertManager
					)
				})
			],
			[
				OfficeDocumentEditor.Local,
				() => ({
					kind: ActionKind.Regular,
					name: "open-in-local-app",
					group: _openGroup,
					title: (c) => {
						const { selection } = c;
						if (selection.length !== 1) {
							return "";
						}

						const item: DocumentItemViewModel = selection[0];
						if (item.itemType === DocumentItemType.Folder) {
							return "";
						}

						return getOpenLocalizations(item)
							.local
							.action;
					},
					icon: "folder-open",
					canExecute: (c) => {
						const { selection } = c;
						if (selection.length !== 1) {
							return false;
						}

						const item: DocumentItemViewModel = selection[0];
						if (item.itemType === DocumentItemType.Folder) {
							return false;
						}
						return calculate(item).canBeOpenedLocally;
					},
					execute: guardAgainstInfectedDocuments(
						(c) => {
							const item: DocumentItemViewModel = c.selection[0] as Exclude<DocumentItemViewModel, FolderViewModel>;

							openInLocalApp(item);

							if (calculate(item).canBeOpenedOnline) {
								showOpeningInLocalEditorWithOptionToOpenInOnlineEditor(
									getOpenLocalizations(item),
									() => openFileInWopi(item)
								);
							}
						},
						alertManager
					)
				})
			]
		],
		editorPreference
	);
}

function guardAgainstInfectedDocuments(
	handler: (context: SharePointDocumentActionContext) => void,
	alertManager: AlertManager
): ((context: SharePointDocumentActionContext) => void) {
	return (context: SharePointDocumentActionContext) => {
		for (const document of context.selection) {
			if (isDocumentInfected(document)) {
				alertManager.showWarning(
					localize("_ThisFileIsInfectedByMalwareToProtectYourPcYouCannotOpenShareOrDownloadThisFile")
				);
				return;
			}
		}
		handler(context);
	};
}

function isDocumentInfected(document: DocumentItemViewModel): boolean {
	if (document.itemType === DocumentItemType.File) {
		return Boolean(document.isInfected);
	}
	return false;
}

export function getActionsForLiveTile(
	context: SharePointDocumentActionContext,
	handlers: OpenActionHandlers,
	alertManager: AlertManager
): readonly SharePointDocumentAction[] {
	if (context.view.type === DocumentsViewType.RecycleBinView) {
		throw new Error("This module does not support the recyclebin");
	}

	const openActions: readonly SharePointDocumentAction[] = getOpenActions(
		handlers,
		context.selection,
		alertManager
	);

	return [
		...openActions,
		createShareLinkAction(alertManager),
		createMultiDownloadAction(alertManager),
		createAttachToEmailAction(alertManager)
	];
}

export function getImagePreviewActions(
	alertManager: AlertManager,
	addBookmarkHandler: AddBookmarkHandler
): readonly SharePointDocumentAction[] {
	const actions: readonly SharePointDocumentAction[] = [
		createDownloadAction(alertManager),
		createShareLinkAction(alertManager),
		createInviteUserAction(alertManager),
		createAttachToEmailAction(alertManager),
		_permissionsAction,
		createAddBookmarkAction(addBookmarkHandler),
		createRemoveBookmarkAction(alertManager)
	];

	return actions;
}